import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { Palette, X, Check } from 'lucide-react';

const STORAGE_KEY = 'iacc_theme';
const THEMES = [
  { id: 'default', bg: '#f8fafc', card: '#ffffff', label: { en: 'Default', ar: 'الافتراضي' } },
  { id: 'slate-1', bg: '#eef2f6', card: '#ffffff', label: { en: 'Warm Slate', ar: 'رمادي دافئ' } },
  { id: 'slate-2', bg: '#e2e8f0', card: '#f8fafc', label: { en: 'Modern SaaS', ar: 'حديث' } },
  { id: 'slate-3', bg: '#dbe4ee', card: '#f1f5f9', label: { en: 'Cool Blue-Gray', ar: 'أزرق-رمادي' } },
  { id: 'slate-4', bg: '#cbd5e1', card: '#ffffff', label: { en: 'Deep Slate', ar: 'رمادي داكن' } },
  { id: 'green', bg: '#dbeafe', card: '#eff6ff', label: { en: 'Blue Tint', ar: 'أزرق الهوية' } },
  { id: 'dark', bg: '#0f172a', card: '#1e293b', label: { en: 'Full Dark', ar: 'وضع ليلي' } },
];

export const THEME_KEY = 'iacc_theme';
export const applyTheme = (id) => {
  const html = document.documentElement;
  const body = document.body;
  ['default', 'slate-1', 'slate-2', 'slate-3', 'slate-4', 'green', 'dark'].forEach((t) => body.classList.remove(`theme-${t}`));
  if (!id || id === 'default') {
    html.removeAttribute('data-theme');
  } else {
    html.setAttribute('data-theme', id);
    body.classList.add(`theme-${id}`);
  }
};

export const ThemePicker = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('default');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) || 'default';
    setCurrent(saved);
    applyTheme(saved);
  }, []);

  const pick = (id) => {
    setCurrent(id);
    localStorage.setItem(STORAGE_KEY, id);
    applyTheme(id);
  };

  return (
    <div className="fixed bottom-5 end-5 z-[70]" data-testid="theme-picker">
      {open ? (
        <div className="bg-white rounded-2xl shadow-2xl border border-[#e5e7eb] p-4 w-64" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-[#2563eb]" />
              <div className="text-sm font-bold text-[#0a0a0a]">{lang === 'ar' ? 'نموذج الألوان' : 'Color Theme'}</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#111827]/40 hover:text-[#111827]" data-testid="theme-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {THEMES.map((t) => (
              <button
                key={t.id}
                onClick={() => pick(t.id)}
                className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-all ${current === t.id ? 'border-[#2563eb] bg-[#2563eb]/5' : 'border-[#e5e7eb] hover:border-[#2563eb]/40'}`}
                data-testid={`theme-${t.id}`}
              >
                <div className="flex-shrink-0 flex gap-0.5">
                  <div className="w-6 h-8 rounded-l border border-[#e5e7eb]" style={{ background: t.bg }} />
                  <div className="w-6 h-8 rounded-r border border-[#e5e7eb]" style={{ background: t.card }} />
                </div>
                <div className="flex-1 text-start text-xs font-medium text-[#0a0a0a]">{t.label[lang] || t.label.en}</div>
                {current === t.id && <Check className="w-4 h-4 text-[#2563eb]" />}
              </button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-[#e5e7eb] text-[10px] text-[#111827]/50 text-center">
            {lang === 'ar' ? 'الاختيار يُحفظ محلياً على جهازك' : 'Your pick is saved locally in this browser'}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="bg-[#0a0a0a] text-white rounded-full w-12 h-12 shadow-2xl flex items-center justify-center hover:bg-[#2563eb] transition-colors"
          title={lang === 'ar' ? 'تغيير النموذج اللوني' : 'Change color theme'}
          data-testid="theme-picker-toggle"
        >
          <Palette className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default ThemePicker;
